// Web/DOM port of teamplayer/app/view-routes/_components/route-replay-controls.tsx
// Play/pause button, draggable progress track (native pointer events),
// speed chips, current/end clock, and an "idle for X" caption.

import React, { useCallback, useRef } from "react";
import { BsPlayFill, BsPauseFill } from "react-icons/bs";

const SPEEDS = ["slow", "normal", "fast"];
const SPEED_LABEL = { slow: "Slow", normal: "Normal", fast: "Fast" };

const formatClock = (ts, withSeconds = true) => {
  if (!Number.isFinite(ts) || ts <= 0) return withSeconds ? "--:--:--" : "--:--";
  const opts = { hour: "2-digit", minute: "2-digit" };
  if (withSeconds) opts.second = "2-digit";
  return new Date(ts).toLocaleTimeString("en-US", opts);
};

const formatHaltDuration = (seconds) => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  }
  return `${minutes} min`;
};

function RouteReplayControls({
  visible,
  isPlaying,
  canPlay,
  progress,
  speed,
  virtualTime,
  endTs,
  activeHaltDuration,
  onToggle,
  onSeek,
  onSpeedChange,
  onScrubStart,
  onScrubEnd,
}) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const lastSeekRef = useRef(0);

  const seekFromClientX = useCallback(
    (clientX, force = false) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const local = Math.min(rect.width, Math.max(0, clientX - rect.left));
      const now = Date.now();
      if (!force && now - lastSeekRef.current < 33) return;
      lastSeekRef.current = now;
      onSeek(local / rect.width);
    },
    [onSeek]
  );

  const handlePointerDown = useCallback(
    (e) => {
      if (!canPlay) return;
      e.preventDefault();
      draggingRef.current = true;
      lastSeekRef.current = 0;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* setPointerCapture unsupported — drag still works via move events */
      }
      onScrubStart && onScrubStart();
      seekFromClientX(e.clientX, true);
    },
    [canPlay, onScrubStart, seekFromClientX]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      seekFromClientX(e.clientX);
    },
    [seekFromClientX]
  );

  const endDrag = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      if (e && typeof e.clientX === "number") seekFromClientX(e.clientX, true);
      onScrubEnd && onScrubEnd();
    },
    [onScrubEnd, seekFromClientX]
  );

  if (!visible) return null;

  const progressPct = Math.min(1, Math.max(0, progress)) * 100;
  const isHaltActive = activeHaltDuration != null;

  return (
    <div style={styles.container}>
      <div style={styles.panel}>
        <div style={styles.row}>
          <button
            type="button"
            onClick={onToggle}
            disabled={!canPlay}
            style={{
              ...styles.playButton,
              ...(canPlay ? {} : styles.playButtonDisabled),
            }}
          >
            {isPlaying ? (
              <BsPauseFill size={22} color="#fff" />
            ) : (
              <BsPlayFill size={22} color="#fff" />
            )}
          </button>

          <div style={styles.middle}>
            <div style={styles.timeRow}>
              <span style={styles.timeText}>{formatClock(virtualTime, true)}</span>
              <span style={styles.label}>Route Replay</span>
              <span style={styles.timeText}>{formatClock(endTs, false)}</span>
            </div>

            <div
              ref={trackRef}
              style={styles.track}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <div style={styles.trackBg} />
              <div style={{ ...styles.trackFill, width: `${progressPct}%` }} />
              <div style={{ ...styles.thumb, left: `${progressPct}%` }} />
            </div>

            {isHaltActive && (
              <div style={styles.haltCaption}>
                ⏸ Idle for {formatHaltDuration(activeHaltDuration)}
              </div>
            )}
          </div>
        </div>

        <div style={styles.speedRow}>
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSpeedChange(s)}
              style={{
                ...styles.speedChip,
                ...(speed === s ? styles.speedChipActive : {}),
              }}
            >
              <span
                style={{
                  ...styles.speedChipText,
                  ...(speed === s ? styles.speedChipTextActive : {}),
                }}
              >
                {SPEED_LABEL[s]}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: "absolute",
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(420px, calc(100% - 40px))",
    zIndex: 20,
  },
  panel: {
    background: "white",
    borderRadius: "12px",
    padding: "12px 14px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
    fontFamily: "Noto Sans, sans-serif",
  },
  row: { display: "flex", alignItems: "center" },
  playButton: {
    width: "44px",
    height: "44px",
    borderRadius: "22px",
    background: "#2563EB",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    cursor: "pointer",
    flexShrink: 0,
  },
  playButtonDisabled: { background: "#9CA3AF", cursor: "not-allowed" },
  middle: { flex: 1, minWidth: 0 },
  timeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
  },
  timeText: { fontSize: "11px", color: "#6B7280", fontVariantNumeric: "tabular-nums" },
  label: { fontSize: "11px", fontWeight: 600, color: "#374151" },
  track: {
    height: "24px",
    display: "flex",
    alignItems: "center",
    position: "relative",
    cursor: "pointer",
    touchAction: "none",
  },
  trackBg: {
    position: "absolute",
    left: 0,
    right: 0,
    height: "4px",
    borderRadius: "2px",
    background: "#E5E7EB",
  },
  trackFill: {
    position: "absolute",
    left: 0,
    height: "4px",
    borderRadius: "2px",
    background: "#2563EB",
  },
  thumb: {
    position: "absolute",
    width: "14px",
    height: "14px",
    borderRadius: "7px",
    background: "#2563EB",
    border: "2px solid white",
    marginLeft: "-7px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
  },
  haltCaption: {
    fontSize: "11px",
    color: "#EF4444",
    fontWeight: 600,
    marginTop: "4px",
  },
  speedRow: {
    display: "flex",
    marginTop: "10px",
    background: "#F3F4F6",
    borderRadius: "8px",
    padding: "3px",
  },
  speedChip: {
    flex: 1,
    padding: "6px 0",
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },
  speedChipActive: {
    background: "white",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },
  speedChipText: { fontSize: "12px", color: "#6B7280", fontWeight: 500 },
  speedChipTextActive: { color: "#2563EB", fontWeight: 700 },
};

export default React.memo(RouteReplayControls);
